import React from "react";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import MenuIcon from "@mui/icons-material/Menu";
import {Spa} from "@mui/icons-material";
import Box from "@mui/material/Box";
import useScrollTrigger from "@mui/material/useScrollTrigger";
import {UserButton} from "./UserButton";
import {MENU_CLOSED, MENU_OPENED, useHeaderDispatch, useHeaderState} from "./headerContext";
import {useLocation, useNavigate} from "react-router";
import {useSmoothScroll} from "../app/useSmoothScroll";
import {useActiveSection} from "../app/useActiveSection";

const NAV_ITEMS = [
    {label: "Om kurset", path: "/about", sectionId: "om-kurset"},
    {label: "Meditasjon", path: "/meditation", sectionId: "meditasjon"},
    {label: "Erfaringer", path: "/#erfaringer", sectionId: "erfaringer"},
    {label: "Finn kursholder", path: "/find-instructor", sectionId: "finn-kursholder"},
    {label: "Aktuelt", path: "/news", sectionId: "aktuelt"},
    {label: "For fagfolk", path: "/get-educated", sectionId: "for-fagfolk"},
    {label: "Bestill bøker", path: "/order-books"},
];

const SECTION_IDS = ["om-kurset", "meditasjon", "erfaringer", "finn-kursholder", "aktuelt", "for-fagfolk"];

const SECTION_BG_MAP = {
    "om-kurset": "rgba(255, 255, 255, 0.90)",
    "meditasjon": "rgba(243, 247, 244, 0.90)",
    "finn-kursholder": "rgba(255, 255, 255, 0.90)",
    "erfaringer": "rgba(250, 248, 245, 0.90)",
    "aktuelt": "rgba(255, 255, 255, 0.90)",
    "for-fagfolk": "rgba(248, 246, 241, 0.90)",
};

const getHeaderBackground = (activeSection, isLanding) => {
    if (!isLanding) {
        return "rgba(255, 255, 255, 0.92)";
    }
    if (activeSection && SECTION_BG_MAP[activeSection]) {
        return SECTION_BG_MAP[activeSection];
    }
    return "rgba(250, 248, 245, 0.88)";
};

export const Header = () => {
    const {menuOpen} = useHeaderState();
    const dispatch = useHeaderDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const smoothScroll = useSmoothScroll();
    const activeSection = useActiveSection(SECTION_IDS);

    const trigger = useScrollTrigger({
        disableHysteresis: true,
        threshold: 10,
    });

    const isLanding = location.pathname === "/";

    const toggleMenu = () => {
        if (menuOpen) {
            dispatch({type: MENU_CLOSED});
        } else {
            dispatch({type: MENU_OPENED});
        }
    };

    const handleNavClick = (item) => {
        if (item.sectionId) {
            smoothScroll(item.sectionId);
        } else {
            navigate(item.path);
        }
    };

    const handleLogoClick = () => {
        if (typeof window !== "undefined" && location.pathname === "/") {
            const prefersReducedMotion =
                window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
            window.scrollTo({
                top: 0,
                behavior: prefersReducedMotion ? "auto" : "smooth",
            });
        }
        navigate("/");
    };

    return (
        <AppBar
            position="sticky"
            elevation={0}
            sx={{
                backgroundColor: getHeaderBackground(activeSection, isLanding),
                color: "text.primary",
                borderBottom: "1px solid",
                borderColor: trigger ? "rgba(40, 82, 56, 0.12)" : "rgba(40, 82, 56, 0.06)",
                boxShadow: trigger ? "0 4px 20px rgba(0, 0, 0, 0.05)" : "none",
                backdropFilter: "blur(12px)",
                WebkitBackdropFilter: "blur(12px)",
                transition: "background-color 0.3s ease, box-shadow 0.2s ease, border-color 0.2s ease",
                zIndex: (theme) => theme.zIndex.drawer + 1,
            }}
        >
            <Toolbar sx={{minHeight: {xs: 56, sm: 64, md: 68}, px: {xs: 1, sm: 2, md: 3}, maxWidth: "100%", width: "100%", minWidth: 0, overflow: "hidden", boxSizing: "border-box"}}>
                <Box
                    sx={{
                        display: {
                            xs: "block",
                            md: "none",
                        },
                        flexShrink: 0,
                    }}
                >
                    <IconButton
                        edge="start"
                        sx={{
                            mr: {xs: 0, sm: 1},
                            color: "text.primary",
                            "&:focus-visible": {
                                outline: "2px solid #285238",
                                outlineOffset: 2,
                            },
                        }}
                        aria-label="Hovedmeny"
                        aria-expanded={menuOpen}
                        onClick={toggleMenu}
                        size="large"
                    >
                        <MenuIcon />
                    </IconButton>
                </Box>

                <Box
                    onClick={handleLogoClick}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleLogoClick();
                        }
                    }}
                    role="link"
                    tabIndex={0}
                    aria-label="Gå til forsiden"
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        cursor: "pointer",
                        userSelect: "none",
                        minWidth: 0,
                        flexShrink: 1,
                        mr: {xs: 0.5, md: 2, lg: 3},
                        py: 0.5,
                        borderRadius: 2,
                        outline: "none",
                        "&:focus-visible": {
                            outline: "2px solid #285238",
                            outlineOffset: 2,
                        },
                    }}
                >
                    <Spa
                        sx={{
                            color: "primary.main",
                            fontSize: {xs: 20, sm: 28},
                            mr: 0.5,
                            flexShrink: 0,
                        }}
                    />
                    <Typography
                        variant="h6"
                        component="div"
                        sx={{
                            fontWeight: 700,
                            fontSize: {xs: "0.8rem", sm: "1.1rem", md: "1.25rem"},
                            color: "primary.main",
                            whiteSpace: "nowrap",
                            letterSpacing: "-0.01em",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                        }}
                    >
                        Bekymringsmestring
                    </Typography>
                </Box>

                <Box
                    component="nav"
                    aria-label="Hovednavigasjon"
                    sx={{
                        display: {xs: "none", md: "flex"},
                        alignItems: "center",
                        gap: {md: 0.25, lg: 0.5},
                        ml: {md: 0.5, lg: 1},
                    }}
                >
                    {NAV_ITEMS.map((item) => {
                        const isActive =
                            location.pathname === "/" && item.sectionId
                                ? activeSection === item.sectionId
                                : location.pathname === item.path;

                        return (
                            <Button
                                key={item.path}
                                component="a"
                                role="link"
                                href={item.path}
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleNavClick(item);
                                }}
                                aria-current={isActive ? "page" : undefined}
                                sx={{
                                    textTransform: "none",
                                    fontSize: {md: "0.85rem", lg: "0.925rem"},
                                    fontWeight: isActive ? 700 : 500,
                                    px: {md: 1.25, lg: 2},
                                    py: 0.75,
                                    borderRadius: 3,
                                    whiteSpace: "nowrap",
                                    flexShrink: 0,
                                    color: isActive ? "primary.main" : "text.secondary",
                                    backgroundColor: isActive ? "rgba(40, 82, 56, 0.08)" : "transparent",
                                    transition: "all 0.15s ease-in-out",
                                    "&:hover": {
                                        backgroundColor: isActive
                                            ? "rgba(40, 82, 56, 0.14)"
                                            : "rgba(0, 0, 0, 0.04)",
                                        color: "primary.main",
                                    },
                                    "&:focus-visible": {
                                        outline: "2px solid #285238",
                                        outlineOffset: 2,
                                    },
                                }}
                            >
                                {item.label}
                            </Button>
                        );
                    })}
                </Box>

                <Box sx={{flexGrow: 1}} />
                <UserButton />
            </Toolbar>
        </AppBar>
    );
};